import { StyleSheet, View } from "react-native";

import type { UiTheme } from "../theme";

// <MiniTrendBars> — a compact discrete bar-sparkline for a short series of
// weekly buckets (the "last 4 weeks" trend that rides next to a target row).
// Extracted from the hand-rolled bars in ProgramComposer/WeeklyTargetEditorRow
// when the 2nd callsite landed (PG-1 Progress muscle-coverage rows), so the
// editor's target-reference trend and the Progress coverage trend render the
// identical idiom from one place. NOT a LineChart: the datum is discrete weekly
// counts, i.e. bars, not a continuous line.
//
// Bars scale to the series' own max (each trend defines its own axis); the last
// (most-recent) bar is emphasised. Hidden from screen readers by default — the
// caller announces the same numbers in text (avg/last-week), so the bars are
// decorative reinforcement, never the only carrier.

export type MiniTrendBarsProps = {
  theme: UiTheme;
  // Weekly buckets, oldest → newest. Empty renders nothing.
  weeks: number[];
  // Track height in px (bars scale into this). Default 18 (the editor's size).
  height?: number;
  barWidth?: number;
  gap?: number;
  // Colour of the most-recent bar; earlier bars use restColor. Defaults keep
  // the editor's brand-emphasised-last idiom; the coverage row passes the
  // zone colour so the trend agrees with its target bar.
  lastColor?: string;
  restColor?: string;
  // Announce the bars to a screen reader? Off by default (the numbers live in
  // adjacent text) — matches the editor's accessibilityElementsHidden bars.
  accessibleBars?: boolean;
};

export function MiniTrendBars({
  theme,
  weeks,
  height = 18,
  barWidth = 6,
  gap = 3,
  lastColor,
  restColor,
  accessibleBars = false,
}: MiniTrendBarsProps) {
  const styles = makeStyles(theme);
  if (weeks.length === 0) return null;
  const max = Math.max(1, ...weeks);
  const emphasis = lastColor ?? theme.palette.brand500;
  const quiet = restColor ?? theme.palette.ink3;
  const a11y = accessibleBars
    ? {}
    : {
        accessibilityElementsHidden: true,
        importantForAccessibility: "no-hide-descendants" as const,
      };
  return (
    <View style={[styles.row, { height, gap }]} {...a11y}>
      {weeks.map((w, idx) => (
        <View
          key={idx}
          style={[
            styles.bar,
            {
              width: barWidth,
              height: Math.max(2, Math.round((w / max) * height)),
              backgroundColor: idx === weeks.length - 1 ? emphasis : quiet,
            },
          ]}
        />
      ))}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { radius: r } = theme;
  return StyleSheet.create({
    row: { flexDirection: "row", alignItems: "flex-end" },
    bar: { borderRadius: r.r1 },
  });
}
